import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { Plan, BillingType, UserType } from '../../types/plan';
import { PlanService } from '../../services/PlanService';
import { PlanAccessValidator } from '../../services/PlanAccessValidator';
import { CreditCard, User, Lock, ArrowLeft } from 'lucide-react';
import AppHeader from '../AppHeader';
import InfoModal from '../InfoModal';
import { CHECKOUT_MESSAGES } from '../../constants/checkout';
import CheckoutFounderTrust from './CheckoutFounderTrust';
import CheckoutPlatformPreview from './CheckoutPlatformPreview';
import CheckoutPlanDetails from './CheckoutPlanDetails';
import CheckoutZipCodeField, { ZipValidationStatus } from './CheckoutZipCodeField';
import CheckoutGuestIdentity from './CheckoutGuestIdentity';
import CheckoutPostPaymentNotice from './CheckoutPostPaymentNotice';
import CheckoutTermsAcceptance from './CheckoutTermsAcceptance';
import { captureInfluencerRefFromSearch, getInfluencerRef } from '../../utils/influencerAttribution';
import { getContractType } from '../../utils/contractSteps';
import { trackInitiateCheckout } from '../../utils/facebookPixel';
import { goToMarketing } from '../../utils/crossDomainNav';

const DEFAULT_BILLING_NUMBER = 'S/N';

interface CheckoutFormData {
  cardNumber: string;
  cardName: string;
  cardExpiry: string;
  cardCVV: string;
  cpfCnpj: string;
  phone: string;
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

interface ResolveCheckoutIdentityResponse {
  success: boolean;
  checkoutIdentityId: string;
  isExistingUser: boolean;
  passwordSet: boolean;
  email: string;
  message?: string;
}

interface CheckCheckoutEmailExistsResponse {
  success: boolean;
  email: string;
  exists: boolean;
}

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isAnonymousGuest, user, logout } = useAuth();

  const [plan, setPlan] = useState<Plan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [zipValidationStatus, setZipValidationStatus] = useState<ZipValidationStatus>('idle');
  const [hasSavedCard, setHasSavedCard] = useState(false);
  const [hasBillingInfo, setHasBillingInfo] = useState(false);
  const [cardLast4Digits, setCardLast4Digits] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [useNewCard, setUseNewCard] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'pix'>('card');
  const [pixData, setPixData] = useState<{
    qrCode: string;
    pixCopyPaste: string;
    expirationDate: string;
    chargeId?: string;
    identityAssurance?: 'authenticated' | 'email-only';
    identityOutcome?: 'existing-user' | 'new-user-password-set' | 'new-user-password-pending';
    email?: string;
  } | null>(null);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discount: number;
    type: 'percentage' | 'fixed';
  } | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'warning' as 'error' | 'warning' | 'info' | 'success',
    redirectTo: ''
  });
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [checkoutConfirmEmail, setCheckoutConfirmEmail] = useState('');
  const [checkoutIdentityId, setCheckoutIdentityId] = useState('');
  const [isIdentityResolved, setIsIdentityResolved] = useState(false);
  const [isResolvingIdentity, setIsResolvingIdentity] = useState(false);
  const [isCheckingCheckoutEmail, setIsCheckingCheckoutEmail] = useState(false);
  const [checkoutEmailExists, setCheckoutEmailExists] = useState<boolean | null>(null);
  const [checkoutPassword, setCheckoutPassword] = useState('');
  const [checkoutConfirmPassword, setCheckoutConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const pixQrSectionRef = useRef<HTMLDivElement | null>(null);
  const guestIdentityRef = useRef<HTMLDivElement | null>(null);
  
  const [formData, setFormData] = useState<CheckoutFormData>({
    cardNumber: '',
    cardName: '',
    cardExpiry: '',
    cardCVV: '',
    cpfCnpj: '',
    phone: '',
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      setIsIdentityResolved(true);
    }
  }, [isAuthenticated]);

  // Obter planId dos parâmetros da URL
  const searchParams = new URLSearchParams(location.search);
  const planId = searchParams.get('planId');
  // Compra personalizada de horas: a quantidade INICIAL vem da URL (ex: /checkout?planId=X&quantity=5).
  // Depois do load, vira state local (selectedQuantity) para o cliente poder ajustar no checkout.
  // O valor é SEMPRE recalculado server-side; aqui usamos apenas para UX (resumo do pedido).
  const requestedQuantityParam = (() => {
    const raw = searchParams.get('quantity');
    if (!raw) return undefined;
    const parsed = parseInt(raw, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
  })();

  // State local da quantidade selecionada no checkout. `null` = ainda não inicializado
  // (será definido pelo useEffect após o plano carregar, considerando os limites min/max).
  const [selectedQuantity, setSelectedQuantity] = useState<number | null>(null);
  // Feedback transiente exibido quando o PIX gerado é invalidado por alteração de quantidade
  const [pixInvalidatedMessage, setPixInvalidatedMessage] = useState<string | null>(null);

  useEffect(() => {
    captureInfluencerRefFromSearch(location.search);
  }, [location.search]);

  const withInfluencerPayload = <T extends Record<string, unknown>>(payload: T): T => {
    const code = getInfluencerRef();
    if (!code) return payload;
    return { ...payload, influencerCode: code };
  };

  useEffect(() => {
    const fetchPlan = async () => {
      if (!planId) {
        goToMarketing('/planos', navigate);
        return;
      }

      // Convidado anônimo da sala não pode comprar nessa sessão — limpa e segue como visitante.
      if (isAnonymousGuest) {
        try {
          await logout();
        } catch (error) {
          console.error('Erro ao encerrar sessão de convidado no checkout:', error);
        }
        // Continua o carregamento do plano no fluxo guest (sem validar role).
      }

      // Usuário autenticado (conta real) mas com perfil incompleto (ex: Google login novo)
      // deve completar o cadastro antes de prosseguir com o checkout
      if (isAuthenticated && user && user.role === 'login-not-completed') {
        navigate('/completar-perfil', {
          replace: true,
          state: { from: { pathname: location.pathname, search: location.search } },
        });
        return;
      }

      try {
        const planDoc = await getDoc(doc(db, 'plans', planId));
        if (!planDoc.exists()) {
          goToMarketing('/planos', navigate);
          return;
        }

        const planData = PlanService.fromFirestore(planDoc.id, planDoc.data());
        
        // Só valida tipo de plano para conta cadastrada (nunca para convidado/visitante).
        if (isAuthenticated && user?.role) {
          const validation = PlanAccessValidator.canAccessPlan(user.role, planData.userType);
          
          if (!validation.canAccess) {
            setPlan(null);
            setModalState({
              isOpen: true,
              title: validation.title || 'Acesso Negado',
              message: validation.message || '',
              variant: validation.variant || 'warning',
              redirectTo: validation.redirectTo || '/planos'
            });
            return;
          }
        }
        
        setPlan(planData);

        // Verificar se o usuário possui cartão salvo
        if (isAuthenticated && user?.uid) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const creditCardToken = userData?.billingInfo?.creditCardToken;
            const last4 = userData?.billingInfo?.cardLast4Digits;
            
            // Verificar se tem billingInfo completa (com endereço)
            if (userData?.billingInfo?.address) {
              setHasBillingInfo(true);
            }
            
            if (creditCardToken) {
              setHasSavedCard(true);
              setCardLast4Digits(last4 || '');
              setCardExpiry(userData?.billingInfo?.cardExpiry || '');
            }
          }
        }
      } catch (error) {
        console.error('Erro ao buscar plano:', error);
        goToMarketing('/planos', navigate);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlan();
  }, [isAuthenticated, isAnonymousGuest, user, planId, navigate, location.pathname, location.search, logout]);

  // Verificar status do pagamento PIX
  useEffect(() => {
    if (!pixData?.chargeId || paymentConfirmed) return;

    const checkPaymentStatus = async () => {
      try {
        const checkPaymentFunction = httpsCallable(functions, 'checkPaymentStatus');
        const result = await checkPaymentFunction({
          chargeId: pixData.chargeId,
          ...(!isAuthenticated && checkoutIdentityId ? { checkoutIdentityId } : {}),
        });
        const response = result.data as { 
          success: boolean; 
          status?: string;
          message?: string;
        };

        if (response.success && response.status === 'CONFIRMED') {
          setPaymentConfirmed(true);
          setIsCheckingPayment(false);
          
          // Aguardar 2 segundos antes de redirecionar
          setTimeout(() => {
            navigate('/pagamento/sucesso', { 
              state: { 
                chargeId: pixData.chargeId,
                message: 'Pagamento PIX confirmado com sucesso!',
                planName: plan?.name,
                amount: calculateFinalValue(),
                currency: 'BRL',
                contractType: plan ? getContractType(plan) : undefined,
                identityAssurance: pixData.identityAssurance || (isAuthenticated ? 'authenticated' : 'email-only'),
                identityOutcome: pixData.identityOutcome || 'existing-user',
                checkoutEmail: pixData.email || checkoutEmail,
              } 
            });
          }, 2000);
        }
      } catch (error) {
        console.error('Erro ao verificar status do pagamento:', error);
      }
    };

    // Iniciar polling a cada 5 segundos
    setIsCheckingPayment(true);
    const interval = setInterval(checkPaymentStatus, 5000);
    
    // Verificar imediatamente
    checkPaymentStatus();

    // Limpar intervalo após 15 minutos (tempo máximo de espera)
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setIsCheckingPayment(false);
    }, 15 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [pixData, paymentConfirmed, navigate, plan, isAuthenticated, checkoutIdentityId, checkoutEmail]);

  useEffect(() => {
    if (!pixData || paymentConfirmed) return;

    const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobileViewport) return;

    // Quando o QR Code é gerado, leva o usuário até a seção para escanear sem precisar procurar na tela.
    const scrollToQrCode = () => {
      pixQrSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const timeoutId = window.setTimeout(scrollToQrCode, 100);
    return () => window.clearTimeout(timeoutId);
  }, [pixData, paymentConfirmed]);

  const handleInputChange = (field: keyof CheckoutFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Formatação de inputs
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted.substring(0, 19); // 16 dígitos + 3 espaços
  };

  const formatExpiry = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 4)}`;
    }
    return cleaned;
  };

  const formatCpfCnpj = (value: string) => {
    const cleaned = value.replace(/\D/g, '');

    // CPF: 11 dígitos -> 000.000.000-00
    if (cleaned.length <= 11) {
      const cpf = cleaned.substring(0, 11);
      return cpf
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2');
    }

    // CNPJ: 14 dígitos -> 00.000.000/0000-00
    const cnpj = cleaned.substring(0, 14);
    return cnpj
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
      .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5');
  };

  const isValidCpf = (cpf: string) => {
    if (!cpf || cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
      return false;
    }

    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += Number(cpf.charAt(i)) * (10 - i);
    }

    let firstCheckDigit = (sum * 10) % 11;
    if (firstCheckDigit === 10) {
      firstCheckDigit = 0;
    }

    if (firstCheckDigit !== Number(cpf.charAt(9))) {
      return false;
    }

    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += Number(cpf.charAt(i)) * (11 - i);
    }

    let secondCheckDigit = (sum * 10) % 11;
    if (secondCheckDigit === 10) {
      secondCheckDigit = 0;
    }

    return secondCheckDigit === Number(cpf.charAt(10));
  };

  const isValidCnpj = (cnpj: string) => {
    if (!cnpj || cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) {
      return false;
    }

    const calculateCheckDigit = (base: string, weights: number[]) => {
      const total = base
        .split('')
        .reduce((acc, digit, index) => acc + Number(digit) * weights[index], 0);
      const remainder = total % 11;
      return remainder < 2 ? 0 : 11 - remainder;
    };

    const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    const firstCheckDigit = calculateCheckDigit(cnpj.slice(0, 12), firstWeights);
    const secondCheckDigit = calculateCheckDigit(cnpj.slice(0, 12) + firstCheckDigit, secondWeights);

    return (
      firstCheckDigit === Number(cnpj.charAt(12)) &&
      secondCheckDigit === Number(cnpj.charAt(13))
    );
  };

  const isValidCpfOrCnpj = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) {
      return isValidCpf(digits);
    }
    if (digits.length === 14) {
      return isValidCnpj(digits);
    }
    return false;
  };

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .substring(0, 15);
  };

  const formatZipCode = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.replace(/(\d{5})(\d)/, '$1-$2').substring(0, 9);
  };

  const getBillingAddressPayload = () => ({
    zipCode: formData.zipCode,
    street: formData.street,
    number: formData.number || DEFAULT_BILLING_NUMBER,
    complement: formData.complement || '',
    neighborhood: formData.neighborhood,
    city: formData.city,
    state: formData.state,
  });

  const assertZipValidated = (): boolean => {
    if (hasBillingInfo) {
      return true;
    }

    const cleanedZip = formData.zipCode.replace(/\D/g, '');
    if (cleanedZip.length !== 8 || zipValidationStatus !== 'valid' || !formData.city || !formData.state) {
      setModalState({
        isOpen: true,
        title: 'CEP inválido',
        message:
          zipValidationStatus === 'invalid'
            ? CHECKOUT_MESSAGES.validation.zipInvalid
            : CHECKOUT_MESSAGES.validation.zipRequired,
        variant: 'warning',
        redirectTo: '',
      });
      return false;
    }
    return true;
  };

  const validateZipCode = async (zipCode: string) => {
    const cleanedZip = zipCode.replace(/\D/g, '');
    if (cleanedZip.length !== 8) {
      return;
    }

    setIsLoadingAddress(true);
    setZipValidationStatus('idle');

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanedZip}/json/`);
      const data = await response.json();

      if (data.erro) {
        setZipValidationStatus('invalid');
        setFormData((prev) => ({
          ...prev,
          street: '',
          neighborhood: '',
          city: '',
          state: '',
          number: '',
        }));
        return;
      }

      const city = data.localidade || '';
      const state = data.uf || '';
      setFormData((prev) => ({
        ...prev,
        street: (data.logradouro || '').trim() || 'Não informado',
        neighborhood: (data.bairro || '').trim() || city || 'Centro',
        city,
        state,
        number: DEFAULT_BILLING_NUMBER,
        complement: '',
      }));
      setZipValidationStatus('valid');
    } catch (error) {
      console.error('Erro ao validar CEP:', error);
      setZipValidationStatus('invalid');
      setModalState({
        isOpen: true,
        title: 'Erro ao validar CEP',
        message: CHECKOUT_MESSAGES.validation.zipLookupError,
        variant: 'warning',
        redirectTo: '',
      });
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const handleZipCodeChange = (value: string) => {
    const formatted = formatZipCode(value);
    handleInputChange('zipCode', formatted);
    setZipValidationStatus('idle');

    const cleanedZip = formatted.replace(/\D/g, '');
    if (cleanedZip.length === 8) {
      validateZipCode(formatted);
    } else {
      setFormData((prev) => ({
        ...prev,
        street: '',
        neighborhood: '',
        city: '',
        state: '',
        number: '',
        complement: '',
      }));
    }
  };

  const applyCoupon = async () => {
    const trimmedCode = couponCode.trim();
    
    if (!trimmedCode) {
      return;
    }

    setIsApplyingCoupon(true);

    try {
      // Chamar função para validar cupom
      const validateCouponFunction = httpsCallable(functions, 'validateCoupon');
      const result = await validateCouponFunction({ 
        couponCode: trimmedCode,
        planId: plan?.id 
      });
      
      const response = result.data as { 
        success: boolean; 
        discount?: number;
        type?: 'percentage' | 'fixed';
        message?: string;
      };

      if (response.success && response.discount) {
        setAppliedCoupon({
          code: trimmedCode,
          discount: response.discount,
          type: response.type || 'percentage'
        });
        setModalState({
          isOpen: true,
          title: 'Cupom Aplicado!',
          message: `Cupom "${trimmedCode}" aplicado com sucesso.`,
          variant: 'success',
          redirectTo: ''
        });
      } else {
        setAppliedCoupon(null);
        setModalState({
          isOpen: true,
          title: 'Cupom Inválido',
          message: response.message || 'O cupom informado não é válido ou está expirado.',
          variant: 'warning',
          redirectTo: ''
        });
      }
    } catch (error: any) {
      console.error('Erro ao validar cupom:', error);
      setAppliedCoupon(null);
      setModalState({
        isOpen: true,
        title: 'Erro ao Validar Cupom',
        message: error?.message || 'Não foi possível validar o cupom. Tente novamente.',
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleResolveCheckoutIdentity = async () => {
    const normalizedEmail = checkoutEmail.trim().toLowerCase();
    if (!normalizedEmail || !plan?.id) {
      setModalState({
        isOpen: true,
        title: 'Dados Incompletos',
        message: 'Informe um email válido para continuar.',
        variant: 'warning',
        redirectTo: ''
      });
      return;
    }

    let emailExists = checkoutEmailExists;

    if (!isAuthenticated && emailExists === null) {
      setIsCheckingCheckoutEmail(true);
      try {
        const checkCheckoutEmailExistsFunction = httpsCallable(functions, 'checkCheckoutEmailExists');
        const checkResult = await checkCheckoutEmailExistsFunction({
          email: normalizedEmail,
        });

        const checkResponse = checkResult.data as CheckCheckoutEmailExistsResponse;
        if (!checkResponse.success) {
          throw new Error(CHECKOUT_MESSAGES.identity.checkEmailError);
        }

        emailExists = checkResponse.exists;
        setCheckoutEmailExists(checkResponse.exists);

        if (!checkResponse.exists) {
          const newUserReady =
            checkoutConfirmEmail.trim().toLowerCase() === normalizedEmail &&
            checkoutPassword.length >= 6 &&
            checkoutPassword === checkoutConfirmPassword;

          if (!newUserReady) {
            setModalState({
              isOpen: true,
              title: CHECKOUT_MESSAGES.identity.title,
              message: CHECKOUT_MESSAGES.identity.newUserPrompt,
              variant: 'info',
              redirectTo: ''
            });
            return;
          }
        }
      } catch (error: any) {
        const message =
          error?.message ||
          error?.details ||
          CHECKOUT_MESSAGES.identity.checkEmailError;
        setModalState({
          isOpen: true,
          title: 'Não foi possível continuar',
          message,
          variant: 'error',
          redirectTo: ''
        });
        return;
      } finally {
        setIsCheckingCheckoutEmail(false);
      }
    }

    const requiresEmailConfirmation = !isAuthenticated && emailExists === false;
    const isConfirmEmailMatch =
      checkoutConfirmEmail.trim().toLowerCase() === normalizedEmail;

    if (requiresEmailConfirmation && !isConfirmEmailMatch) {
      setModalState({
        isOpen: true,
        title: 'Dados Incompletos',
        message: CHECKOUT_MESSAGES.identity.confirmEmailMismatch,
        variant: 'warning',
        redirectTo: ''
      });
      return;
    }

    if (requiresEmailConfirmation) {
      if (checkoutPassword.length < 6 || checkoutPassword !== checkoutConfirmPassword) {
        setModalState({
          isOpen: true,
          title: 'Senha inválida',
          message:
            checkoutPassword.length < 6
              ? 'A senha deve ter ao menos 6 caracteres.'
              : CHECKOUT_MESSAGES.identity.passwordMismatch,
          variant: 'warning',
          redirectTo: ''
        });
        return;
      }
    }

    setIsResolvingIdentity(true);
    try {
      const resolveCheckoutIdentityFunction = httpsCallable(functions, 'resolveCheckoutIdentity');
      const result = await resolveCheckoutIdentityFunction({
        email: normalizedEmail,
        planId: plan.id,
        setPasswordNow: requiresEmailConfirmation,
        ...(requiresEmailConfirmation ? { password: checkoutPassword } : {}),
      });

      const response = result.data as ResolveCheckoutIdentityResponse;
      if (!response.success || !response.checkoutIdentityId) {
        throw new Error(response.message || CHECKOUT_MESSAGES.identity.resolveError);
      }

      setCheckoutIdentityId(response.checkoutIdentityId);
      setCheckoutEmail(response.email || normalizedEmail);
      setCheckoutEmailExists(response.isExistingUser);
      setIsIdentityResolved(true);

      setModalState({
        isOpen: true,
        title: 'Pronto',
        message: response.message || CHECKOUT_MESSAGES.identity.resolveSuccess,
        variant: 'success',
        redirectTo: ''
      });
    } catch (error: any) {
      setIsIdentityResolved(false);
      setCheckoutIdentityId('');
      const message =
        error?.message ||
        error?.details ||
        CHECKOUT_MESSAGES.identity.resolveError;
      setModalState({
        isOpen: true,
        title: 'Não foi possível continuar',
        message,
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setIsResolvingIdentity(false);
    }
  };

  const handleCouponBlur = () => {
    if (couponCode.trim() && !appliedCoupon) {
      applyCoupon();
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  // Campos de confirmação/senha só após saber que o email é conta nova.
  const isEmailConfirmationRequired = !isAuthenticated && checkoutEmailExists === false;
  const isEmailConfirmed =
    checkoutConfirmEmail.trim().toLowerCase() === checkoutEmail.trim().toLowerCase();
  const isNewUserPasswordValid =
    checkoutPassword.length >= 6 && checkoutPassword === checkoutConfirmPassword;
  const canResolveNewUserIdentity =
    checkoutEmail.trim().length > 0 &&
    (checkoutEmailExists === null ||
      (checkoutEmailExists === false && isEmailConfirmed && isNewUserPasswordValid) ||
      checkoutEmailExists === true);

  const resetGuestIdentityOnEmailChange = (email: string) => {
    setCheckoutEmail(email);
    setCheckoutConfirmEmail('');
    setCheckoutEmailExists(null);
    setCheckoutIdentityId('');
    setIsIdentityResolved(false);
    setCheckoutPassword('');
    setCheckoutConfirmPassword('');
  };

  const scrollToGuestIdentity = () => {
    guestIdentityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const assertGuestIdentityForPayment = (): boolean => {
    if (isAuthenticated || isIdentityResolved) {
      return true;
    }
    setModalState({
      isOpen: true,
      title: 'Identificação necessária',
      message:
        paymentMethod === 'pix'
          ? 'Informe seu email (e confirme, se for conta nova), clique em Continuar e só depois gere o PIX.'
          : 'Informe seu email e clique em Continuar para liberar o pagamento.',
      variant: 'warning',
      redirectTo: '',
    });
    scrollToGuestIdentity();
    return false;
  };

  const renderGuestIdentityBlock = () => (
    <div ref={guestIdentityRef}>
      <CheckoutGuestIdentity
        checkoutEmail={checkoutEmail}
        checkoutConfirmEmail={checkoutConfirmEmail}
        checkoutPassword={checkoutPassword}
        checkoutConfirmPassword={checkoutConfirmPassword}
        isEmailConfirmationRequired={isEmailConfirmationRequired}
        canContinue={canResolveNewUserIdentity}
        isResolvingIdentity={isResolvingIdentity}
        isCheckingCheckoutEmail={isCheckingCheckoutEmail}
        isIdentityResolved={isIdentityResolved}
        checkoutEmailExists={checkoutEmailExists}
        onEmailChange={resetGuestIdentityOnEmailChange}
        onConfirmEmailChange={setCheckoutConfirmEmail}
        onPasswordChange={setCheckoutPassword}
        onConfirmPasswordChange={setCheckoutConfirmPassword}
        onContinue={handleResolveCheckoutIdentity}
      />
    </div>
  );

  // Compra personalizada de horas: deriva qty efetiva + preço base recalculado.
  // O backend é a única fonte da verdade — aqui só usamos para UX (resumo do pedido).
  // A quantidade vem do state local `selectedQuantity` (que o cliente pode alterar
  // pelo stepper) com fallback no parâmetro de URL e por fim em `minQty`.
  const customQuantityState = React.useMemo(() => {
    if (!plan) {
      return { supports: false, quantity: 1, pricePerUnit: 0, baseValue: 0, isCustom: false, minQty: 1, maxQty: 1 };
    }
    const supports = PlanService.supportsCustomQuantity(plan);

    if (!supports) {
      return {
        supports: false,
        quantity: plan.quantity || 1,
        pricePerUnit: 0,
        baseValue: plan.value || 0,
        isCustom: false,
        minQty: plan.quantity || 1,
        maxQty: plan.quantity || 1,
      };
    }

    const minQty = Number.isInteger(plan.minQuantity) ? (plan.minQuantity as number) : 1;
    const maxQty = Number.isInteger(plan.maxQuantity) ? (plan.maxQuantity as number) : 50;
    const requested = selectedQuantity ?? requestedQuantityParam ?? minQty;
    const clamped = Math.max(minQty, Math.min(maxQty, requested));
    const pricePerUnit = Math.round(((plan.value || 0) / (plan.quantity || 1)) * 100) / 100;
    const baseValue = Math.round(pricePerUnit * clamped * 100) / 100;
    return {
      supports: true,
      quantity: clamped,
      pricePerUnit,
      baseValue,
      isCustom: clamped !== (plan.quantity || 0),
      minQty,
      maxQty,
    };
  }, [plan, selectedQuantity, requestedQuantityParam]);

  // Inicializa selectedQuantity quando o plano carrega, clampando aos limites do plano.
  // Idempotente: só seta se ainda for null (não sobrescreve mudanças do usuário).
  useEffect(() => {
    if (selectedQuantity !== null) return;
    if (!customQuantityState.supports) return;
    setSelectedQuantity(customQuantityState.quantity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, customQuantityState.supports]);

  /**
   * Altera a quantidade de horas dentro do checkout.
   * - Faz clamp respeitando min/max do plano (proteção UX; backend revalida tudo).
   * - Invalida qualquer PIX já gerado (o QR Code antigo é de outro valor).
   * - Atualiza a URL via replace para refresh-safe.
   * - O cupom (se aplicado) permanece: o recálculo acontece automaticamente via
   *   calculateFinalValue(). O backend valida tudo de novo no submit.
   */
  const handleChangeQuantity = (delta: number) => {
    if (!customQuantityState.supports) return;
    if (isLoading) return;
    const { minQty, maxQty, quantity } = customQuantityState;
    const next = Math.max(minQty, Math.min(maxQty, quantity + delta));
    if (next === quantity) return;

    setSelectedQuantity(next);

    if (pixData) {
      setPixData(null);
      setPixInvalidatedMessage(
        'O PIX anterior foi invalidado porque a quantidade foi alterada. Gere um novo com o valor atualizado.'
      );
    }

    // Sincronizar URL para refresh-safe
    try {
      const newParams = new URLSearchParams(location.search);
      newParams.set('quantity', String(next));
      navigate(`/checkout?${newParams.toString()}`, { replace: true });
    } catch (err) {
      // navegação não é crítica; apenas log
      console.warn('Falha ao sincronizar URL com nova quantidade', err);
    }
  };

  const purchaseBaseValue = customQuantityState.baseValue;

  const calculateFinalValue = () => {
    if (!plan || !appliedCoupon) return purchaseBaseValue;

    if (appliedCoupon.type === 'percentage') {
      return purchaseBaseValue * (1 - appliedCoupon.discount / 100);
    } else {
      return Math.max(0, purchaseBaseValue - appliedCoupon.discount);
    }
  };

  // Facebook Pixel: InitiateCheckout (uma vez por plano carregado)
  const initiateCheckoutFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!plan?.id) return;
    if (initiateCheckoutFiredRef.current === plan.id) return;
    initiateCheckoutFiredRef.current = plan.id;
    trackInitiateCheckout({ value: purchaseBaseValue || plan.value, contentName: plan.name });
  }, [plan?.id, plan?.name, plan?.value, purchaseBaseValue]);

  const assertTermsAccepted = (): boolean => {
    if (acceptedTerms) return true;

    setModalState({
      isOpen: true,
      title: 'Aceite os termos',
      message: CHECKOUT_MESSAGES.validation.termsRequired,
      variant: 'warning',
      redirectTo: '',
    });
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      if (!plan) {
        throw new Error('Plano não encontrado');
      }

      if (!assertGuestIdentityForPayment()) {
        setIsProcessing(false);
        return;
      }

      if (!assertTermsAccepted()) {
        setIsProcessing(false);
        return;
      }

      // Validar campos obrigatórios
      if (!formData.cardNumber || !formData.cardName || !formData.cardExpiry || !formData.cardCVV) {
        setModalState({
          isOpen: true,
          title: 'Dados Incompletos',
          message: 'Por favor, preencha todos os dados do cartão.',
          variant: 'warning',
          redirectTo: ''
        });
        setIsProcessing(false);
        return;
      }

      if (!formData.cpfCnpj || !formData.phone) {
        setModalState({
          isOpen: true,
          title: 'Dados Incompletos',
          message: 'Por favor, preencha CPF/CNPJ e telefone.',
          variant: 'warning',
          redirectTo: ''
        });
        setIsProcessing(false);
        return;
      }

      if (!isValidCpfOrCnpj(formData.cpfCnpj)) {
        setModalState({
          isOpen: true,
          title: 'Documento inválido',
          message: CHECKOUT_MESSAGES.validation.invalidDocument,
          variant: 'warning',
          redirectTo: ''
        });
        setIsProcessing(false);
        return;
      }

      if (!assertZipValidated()) {
        setIsProcessing(false);
        return;
      }

      // Chamar função de pagamento (tokenização feita no backend)
      const processPaymentFunction = httpsCallable(functions, 'processPayment');
      
      const paymentData = {
        planId: plan.id,
        ...(!isAuthenticated && { checkoutIdentityId }),
        cardNumber: formData.cardNumber,
        cardName: formData.cardName,
        cardExpiry: formData.cardExpiry,
        cardCVV: formData.cardCVV,
        cpfCnpj: formData.cpfCnpj,
        phone: formData.phone,
        ...(!hasBillingInfo ? getBillingAddressPayload() : {}),
        ...(appliedCoupon && { couponCode: appliedCoupon.code }),
        ...(customQuantityState.supports && { quantity: customQuantityState.quantity }),
      };

      const result = await processPaymentFunction(withInfluencerPayload(paymentData));
      const response = result.data as { 
        success: boolean; 
        subscriptionId?: string; 
        chargeId?: string;
        invoiceUrl?: string;
        message?: string;
      };

      if (response.success) {
        // Redirecionar para página de sucesso
        navigate('/pagamento/sucesso', { 
          state: { 
            subscriptionId: response.subscriptionId,
            chargeId: response.chargeId,
            invoiceUrl: response.invoiceUrl,
            message: response.message,
            planName: plan.name,
            amount: calculateFinalValue(),
            currency: 'BRL',
            contractType: getContractType(plan),
            identityAssurance: (response as any).identityAssurance,
            identityOutcome: (response as any).identityOutcome,
            checkoutEmail,
          } 
        });
      } else {
        setModalState({
          isOpen: true,
          title: 'Erro no Pagamento',
          message: response.message || 'Não foi possível processar o pagamento. Verifique os dados e tente novamente.',
          variant: 'error',
          redirectTo: ''
        });
      }
    } catch (error: any) {
      console.error('Erro ao processar pagamento:', error);
      
      let errorMessage = 'Erro ao processar pagamento. Tente novamente.';
      
      if (error?.code === 'unauthenticated') {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (error?.code === 'invalid-argument') {
        errorMessage = error?.message || 'Dados inválidos. Verifique as informações e tente novamente.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setModalState({
        isOpen: true,
        title: 'Erro no Pagamento',
        message: errorMessage,
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayWithSavedCard = async () => {
    setIsProcessing(true);

    try {
      if (!plan) {
        throw new Error('Plano não encontrado');
      }

      if (!assertGuestIdentityForPayment()) {
        setIsProcessing(false);
        return;
      }

      if (!assertTermsAccepted()) {
        setIsProcessing(false);
        return;
      }

      // Chamar função para processar com cartão salvo
      const processPaymentWithSavedCardFunction = httpsCallable(functions, 'processPaymentWithSavedCard');
      
      const paymentData = {
        planId: plan.id,
        ...(appliedCoupon && { couponCode: appliedCoupon.code }),
        ...(customQuantityState.supports && { quantity: customQuantityState.quantity }),
      };

      const result = await processPaymentWithSavedCardFunction(withInfluencerPayload(paymentData));
      const response = result.data as { 
        success: boolean; 
        subscriptionId?: string; 
        chargeId?: string;
        invoiceUrl?: string;
        message?: string;
      };

      if (response.success) {
        // Redirecionar para página de sucesso
        navigate('/pagamento/sucesso', { 
          state: { 
            subscriptionId: response.subscriptionId,
            chargeId: response.chargeId,
            invoiceUrl: response.invoiceUrl,
            message: response.message,
            planName: plan.name,
            amount: calculateFinalValue(),
            currency: 'BRL',
            contractType: getContractType(plan)
          } 
        });
      } else {
        setModalState({
          isOpen: true,
          title: 'Erro no Pagamento',
          message: response.message || 'Não foi possível processar o pagamento com o cartão salvo.',
          variant: 'error',
          redirectTo: ''
        });
      }
    } catch (error: any) {
      console.error('Erro ao processar pagamento com cartão salvo:', error);
      
      let errorMessage = 'Erro ao processar pagamento. Tente novamente.';
      
      if (error?.code === 'unauthenticated') {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (error?.code === 'failed-precondition') {
        errorMessage = error?.message || 'Cartão salvo inválido. Use outro cartão.';
        setUseNewCard(true);
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setModalState({
        isOpen: true,
        title: 'Erro no Pagamento',
        message: errorMessage,
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePixPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      if (!plan) {
        throw new Error('Plano não encontrado');
      }

      if (!assertGuestIdentityForPayment()) {
        setIsProcessing(false);
        return;
      }

      if (!assertTermsAccepted()) {
        setIsProcessing(false);
        return;
      }

      // Validar CPF/CNPJ
      if (!formData.cpfCnpj || !isValidCpfOrCnpj(formData.cpfCnpj)) {
        setModalState({
          isOpen: true,
          title: 'Dados Incompletos',
          message: CHECKOUT_MESSAGES.validation.invalidDocument,
          variant: 'warning',
          redirectTo: ''
        });
        setIsProcessing(false);
        return;
      }

      // Chamar função de pagamento PIX
      const processPixPaymentFunction = httpsCallable(functions, 'processPixPayment');
      
      const paymentData = {
        planId: plan.id,
        ...(!isAuthenticated && { checkoutIdentityId }),
        cpfCnpj: formData.cpfCnpj,
        phone: formData.phone || '',
        ...(appliedCoupon && { couponCode: appliedCoupon.code }),
        ...(customQuantityState.supports && { quantity: customQuantityState.quantity }),
      };

      const result = await processPixPaymentFunction(withInfluencerPayload(paymentData));
      const response = result.data as { 
        success: boolean; 
        qrCode?: string;
        pixCopyPaste?: string;
        expirationDate?: string;
        chargeId?: string;
        message?: string;
        identityAssurance?: 'authenticated' | 'email-only';
        identityOutcome?: 'existing-user' | 'new-user-password-set' | 'new-user-password-pending';
        email?: string;
      };

      if (response.success) {
        if (response.qrCode && response.pixCopyPaste) {
          // Armazenar dados do PIX para exibir
          setPixData({
            qrCode: response.qrCode,
            pixCopyPaste: response.pixCopyPaste,
            expirationDate: response.expirationDate || '',
            chargeId: response.chargeId,
            identityAssurance: response.identityAssurance,
            identityOutcome: response.identityOutcome,
            email: response.email,
          });
          // Limpa o aviso de "PIX invalidado" quando um novo é gerado com sucesso
          setPixInvalidatedMessage(null);
        } else {
          setPaymentConfirmed(true);
          setIsCheckingPayment(false);
          
          navigate('/pagamento/sucesso', { 
            state: { 
              chargeId: response.chargeId,
              message: 'Pagamento PIX confirmado com sucesso!',
              planName: plan?.name,
              amount: calculateFinalValue(),
              currency: 'BRL',
              contractType: plan ? getContractType(plan) : undefined,
              identityAssurance: response.identityAssurance,
              identityOutcome: response.identityOutcome,
              checkoutEmail: response.email || checkoutEmail,
            } 
          });
        }
      } else {
        setModalState({
          isOpen: true,
          title: 'Erro no Pagamento',
          message: response.message || 'Não foi possível gerar o pagamento PIX. Tente novamente.',
          variant: 'error',
          redirectTo: ''
        });
      }
    } catch (error: any) {
      console.error('Erro ao processar pagamento PIX:', error);
      
      let errorMessage = 'Erro ao processar pagamento. Tente novamente.';
      
      if (error?.code === 'unauthenticated') {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (error?.code === 'invalid-argument') {
        errorMessage = error?.message || 'Dados inválidos. Verifique as informações e tente novamente.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setModalState({
        isOpen: true,
        title: 'Erro no Pagamento',
        message: errorMessage,
        variant: 'error',
        redirectTo: ''
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5C528A] mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <InfoModal
          isOpen={modalState.isOpen}
          onClose={() => {
            setModalState({ ...modalState, isOpen: false });
            if (modalState.redirectTo) {
              navigate(modalState.redirectTo);
            } else {
              goToMarketing('/planos', navigate);
            }
          }}
          title={modalState.title || 'Não foi possível abrir o checkout'}
          message={
            modalState.message ||
            'Não encontramos este plano. Escolha um plano novamente.'
          }
          variant={modalState.variant}
          buttonText="Entendi"
        />
      </div>
    );
  }

  const handleCheckoutBack = () => {
    const stateFrom = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
    if (stateFrom?.pathname) {
      navigate(`${stateFrom.pathname}${stateFrom.search ?? ''}`);
      return;
    }

    try {
      const refUrl = document.referrer ? new URL(document.referrer) : null;
      if (refUrl?.hostname.endsWith('constelabrasil.com.br') && refUrl.origin !== window.location.origin) {
        window.location.assign(refUrl.href);
        return;
      }
    } catch {
      // referrer inválido — segue para fallbacks abaixo
    }

    if (location.key !== 'default') {
      navigate(-1);
      return;
    }

    goToMarketing(
      plan?.userType === UserType.THERAPIST
        ? '/planos?secao=terapeuta'
        : '/planos?secao=participante',
      navigate
    );
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gray-50">
      <AppHeader />
      
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-10 sm:pt-24 sm:pb-12 lg:py-8">
        <button
          type="button"
          onClick={handleCheckoutBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 sm:mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Voltar
        </button>

        <div className="mb-6 w-full min-w-0">
          <CheckoutPostPaymentNotice userType={plan.userType} />
        </div>

        <div className="mb-6 w-full min-w-0 lg:hidden">
          <CheckoutFounderTrust />
          <CheckoutPlatformPreview />
        </div>

        <div className="grid w-full min-w-0 grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          {/* Formulário de Checkout */}
          <div className="order-1 min-w-0 lg:col-span-2 lg:order-1">
            <div className="max-w-full rounded-2xl bg-white p-4 shadow-lg sm:p-6 md:p-8">
              <>
                  <h1 className="mb-6 text-2xl font-bold text-gray-900 sm:mb-8 sm:text-3xl">{CHECKOUT_MESSAGES.pageTitle}</h1>

              {!pixData && (
                <div className="mb-8 lg:hidden border border-gray-200 rounded-xl p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cupom de Desconto
                  </label>
                  {!appliedCoupon ? (
                    <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onBlur={handleCouponBlur}
                        placeholder="Digite o cupom"
                        disabled={isApplyingCoupon}
                        className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400 text-sm disabled:bg-gray-100"
                      />
                      <button
                        type="button"
                        onClick={applyCoupon}
                        disabled={isApplyingCoupon || !couponCode.trim()}
                        className="shrink-0 px-4 py-2 bg-[#5C528A] text-white rounded-lg font-semibold hover:bg-[#4A4070] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:whitespace-nowrap"
                      >
                        {isApplyingCoupon ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          'Aplicar'
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <div>
                            <p className="text-sm font-semibold text-green-900">{appliedCoupon?.code}</p>
                            <p className="text-xs text-green-700">
                              {appliedCoupon?.type === 'percentage'
                                ? `${appliedCoupon.discount}% de desconto`
                                : `${PlanService.formatCurrency(appliedCoupon?.discount ?? 0)} de desconto`
                              }
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeCoupon}
                          className="text-green-700 hover:text-green-900"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  {plan.isRecurring && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                      <p className="text-xs font-medium text-amber-800">
                        Desconto do cupom válido para a primeira parcela do plano.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Seleção de Método de Pagamento - Apenas para planos não recorrentes */}
              {plan && !plan.isRecurring && !pixData && (
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Método de Pagamento</h2>
                  <div className="grid grid-cols-1 gap-3 min-[380px]:grid-cols-2 min-[380px]:gap-4">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={`p-3 sm:p-4 border-2 rounded-xl transition-all ${
                        paymentMethod === 'card'
                          ? 'border-[#5C528A] bg-purple-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <CreditCard className={`w-8 h-8 mx-auto mb-2 ${
                        paymentMethod === 'card' ? 'text-[#5C528A]' : 'text-gray-600'
                      }`} />
                      <p className={`text-center text-sm font-semibold sm:text-base ${
                        paymentMethod === 'card' ? 'text-[#5C528A]' : 'text-gray-700'
                      }`}>
                        Cartão de Crédito
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('pix')}
                      className={`p-3 sm:p-4 border-2 rounded-xl transition-all ${
                        paymentMethod === 'pix'
                          ? 'border-[#5C528A] bg-purple-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <svg
                        className={`w-8 h-8 mx-auto mb-2 ${
                          paymentMethod === 'pix' ? 'text-[#5C528A]' : 'text-gray-600'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18L19.82 8 12 11.82 4.18 8 12 4.18zM4 10.18l7 3.5v6.14l-7-3.5v-6.14zm16 0v6.14l-7 3.5v-6.14l7-3.5z"/>
                      </svg>
                      <p className={`font-semibold ${
                        paymentMethod === 'pix' ? 'text-[#5C528A]' : 'text-gray-700'
                      }`}>
                        PIX
                      </p>
                    </button>
                  </div>
                </div>
              )}

              {/* Exibir QR Code do PIX se gerado */}
              {pixData && (
                <div className="space-y-6">
                  {/* Indicador de pagamento confirmado */}
                  {paymentConfirmed && (
                    <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-6 text-center animate-pulse">
                      <div className="flex justify-center mb-4">
                        <div className="bg-green-500 rounded-full p-3">
                          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold text-green-800 mb-2">
                        Pagamento Confirmado!
                      </h2>
                      <p className="text-green-700">
                        Redirecionando você em instantes...
                      </p>
                    </div>
                  )}

                  {/* Indicador de verificação de pagamento */}
                  {!paymentConfirmed && isCheckingPayment && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        <p className="text-sm text-blue-800">
                          Aguardando confirmação do pagamento...
                        </p>
                      </div>
                    </div>
                  )}

                  <div ref={pixQrSectionRef} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-4 sm:p-8 text-center overflow-x-hidden">
                    <div className="bg-white rounded-xl p-3 sm:p-6 inline-block mb-6 max-w-full">
                      <img 
                        src={`data:image/png;base64,${pixData.qrCode}`} 
                        alt="QR Code PIX" 
                        className="w-52 h-52 sm:w-64 sm:h-64 max-w-full mx-auto"
                      />
                    </div>
                    
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Escaneie o QR Code para pagar
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Use o aplicativo do seu banco para escanear o código acima
                    </p>

                    {pixData.expirationDate && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                        <p className="text-sm text-yellow-800">
                          ⏰ Este código expira em: <strong>{new Date(pixData.expirationDate).toLocaleString('pt-BR')}</strong>
                        </p>
                      </div>
                    )}

                    <div className="bg-white rounded-lg p-4 mb-6 max-w-full overflow-x-hidden">
                      <p className="text-sm text-gray-600 mb-2">Ou copie o código PIX:</p>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-full">
                        <input
                          type="text"
                          value={pixData.pixCopyPaste}
                          readOnly
                          className="flex-1 min-w-0 w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(pixData.pixCopyPaste);
                            setModalState({
                              isOpen: true,
                              title: 'Código Copiado!',
                              message: 'O código PIX foi copiado para a área de transferência.',
                              variant: 'success',
                              redirectTo: ''
                            });
                          }}
                          className="w-full sm:w-auto px-4 py-2 bg-[#5C528A] text-white rounded-lg hover:bg-[#4A4070] transition-colors whitespace-nowrap"
                        >
                          Copiar
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3 text-left">
                      <h3 className="font-semibold text-gray-900">Como pagar:</h3>
                      <ol className="space-y-2 text-sm text-gray-600">
                        <li className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-[#5C528A] text-white rounded-full flex items-center justify-center text-xs">1</span>
                          <span>Abra o aplicativo do seu banco</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-[#5C528A] text-white rounded-full flex items-center justify-center text-xs">2</span>
                          <span>Escolha pagar com PIX</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-[#5C528A] text-white rounded-full flex items-center justify-center text-xs">3</span>
                          <span>Escaneie o QR Code ou cole o código</span>
                        </li>
                        <li className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-[#5C528A] text-white rounded-full flex items-center justify-center text-xs">4</span>
                          <span>Confirme o pagamento</span>
                        </li>
                      </ol>
                    </div>

                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        💡 Após confirmar o pagamento, seu plano será ativado automaticamente em alguns instantes.
                      </p>
                    </div>

                    <button
                      onClick={() => navigate('/painel')}
                      className="mt-6 w-full bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                    >
                      Ir para o Painel
                    </button>
                  </div>
                </div>
              )}

              {/* Formulário de Pagamento - Ocultar se PIX foi gerado */}
              {!pixData && (
                <>
                  {/* Formulário PIX */}
                  {paymentMethod === 'pix' && (
                    <>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6">
                        <div className="flex items-start gap-3">
                          <svg className="w-6 h-6 text-[#5C528A] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18L19.82 8 12 11.82 4.18 8 12 4.18zM4 10.18l7 3.5v6.14l-7-3.5v-6.14zm16 0v6.14l-7 3.5v-6.14l7-3.5z"/>
                          </svg>
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-1">Pagamento via PIX</h3>
                            <p className="text-sm text-gray-600">
                              Identifique-se com email antes de gerar o QR Code. Conta nova: confirme o email e crie uma senha.
                            </p>
                          </div>
                        </div>
                      </div>

                      {isAuthenticated && user?.email && (
                        <div className="mb-6 rounded-2xl border border-[#D8D1F0] bg-[#F7F4FF] p-5 space-y-3">
                          <h2 className="text-lg font-semibold text-gray-900">
                            {CHECKOUT_MESSAGES.identity.loggedInPixTitle}
                          </h2>
                          <p className="text-sm text-gray-600">
                            {CHECKOUT_MESSAGES.identity.loggedInPixDescription(user.email)}
                          </p>
                          <button
                            type="button"
                            onClick={() => logout()}
                            className="text-sm font-semibold text-[#5C528A] hover:text-[#4A4070] underline"
                          >
                            Sair e usar outro email
                          </button>
                        </div>
                      )}

                      {!isAuthenticated && renderGuestIdentityBlock()}

                    <form
                      id="pix-form"
                      onSubmit={handlePixPayment}
                      onKeyDown={(e) => {
                        if (
                          e.key === 'Enter' &&
                          !isAuthenticated &&
                          !isIdentityResolved &&
                          (e.target as HTMLElement).tagName !== 'TEXTAREA'
                        ) {
                          e.preventDefault();
                        }
                      }}
                      className="space-y-6"
                    >
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <User className="w-5 h-5" />
                          Dados para Pagamento
                        </h2>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              CPF ou CNPJ *
                            </label>
                            <input
                              type="text"
                              required
                              value={formData.cpfCnpj}
                              onChange={(e) => handleInputChange('cpfCnpj', formatCpfCnpj(e.target.value))}
                              placeholder="Digite seu CPF ou CNPJ"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Telefone (opcional)
                            </label>
                            <input
                              type="text"
                              value={formData.phone}
                              onChange={(e) => handleInputChange('phone', formatPhone(e.target.value))}
                              placeholder="(00) 00000-0000"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                            />
                          </div>
                        </div>
                      </div>

                      <CheckoutTermsAcceptance
                        checked={acceptedTerms}
                        onChange={setAcceptedTerms}
                        id="checkout-terms-pix"
                      />

                      <button
                        type="submit"
                        disabled={isProcessing || (!isAuthenticated && !isIdentityResolved) || !acceptedTerms}
                        className="w-full bg-[#5C528A] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#4A4070] transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                      >
                        {isProcessing ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Gerando PIX...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18L19.82 8 12 11.82 4.18 8 12 4.18zM4 10.18l7 3.5v6.14l-7-3.5v-6.14zm16 0v6.14l-7 3.5v-6.14l7-3.5z"/>
                            </svg>
                            Gerar QR Code PIX
                          </>
                        )}
                      </button>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-sm text-gray-900 mb-2">Por que PIX?</h4>
                        <ul className="text-xs text-gray-600 space-y-1">
                          <li>• Pagamento instantâneo e seguro</li>
                          <li>• Sem taxas adicionais</li>
                          <li>• Confirmação em tempo real</li>
                          <li>• Acesso imediato após confirmação</li>
                        </ul>
                      </div>
                    </form>

                    </>
                  )}

                  {/* Opção de Cartão Salvo */}
                  {paymentMethod === 'card' && hasSavedCard && !useNewCard && (
                <div className="mb-8">
                  <div className="border-2 border-[#5C528A] rounded-xl p-6 bg-purple-50">
                    <div className="flex items-start gap-4">
                      <CreditCard className="w-8 h-8 text-[#5C528A] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Cartão Salvo
                        </h3>
                        <p className="text-gray-600 mb-4">
                          Usar cartão final •••• {cardLast4Digits}
                        </p>
                        <p className="text-gray-600 mb-4">
                          Vencimento {cardExpiry}
                        </p>
                        <CheckoutTermsAcceptance
                          checked={acceptedTerms}
                          onChange={setAcceptedTerms}
                          id="checkout-terms-saved-card"
                        />
                        <div className="mt-4 flex flex-col sm:flex-row gap-3">
                          <button
                            type="button"
                  
                            onClick={handlePayWithSavedCard}
                            disabled={isProcessing || !acceptedTerms}
                            className="w-full sm:w-auto bg-[#5C528A] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#4A4070] transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                          >
                            {isProcessing ? (
                              <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Processando...
                              </>
                            ) : (
                              <>
                                <Lock className="w-5 h-5" />
                                Finalizar Compra
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setUseNewCard(true)}
                            className="w-full sm:w-auto border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                          >
                            Usar outro cartão
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Mostrar formulário completo se não tiver cartão salvo ou escolher usar novo */}
              {paymentMethod === 'card' && (!hasSavedCard || useNewCard) && (
                <>
                  {hasSavedCard && useNewCard && (
                    <div className="mb-6">
                      <button
                        type="button"
                        onClick={() => setUseNewCard(false)}
                        className="text-[#5C528A] hover:text-[#4A4070] font-medium flex items-center gap-2"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Voltar para cartão salvo
                      </button>
                    </div>
                  )}

              <form id="card-form" onSubmit={handleSubmit} className="space-y-8">
                {/* Dados do Cartão */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Dados do Cartão
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Número do Cartão *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.cardNumber}
                        onChange={(e) => handleInputChange('cardNumber', formatCardNumber(e.target.value))}
                        placeholder="Digite os 16 dígitos do cartão"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome no Cartão *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.cardName}
                        onChange={(e) => handleInputChange('cardName', e.target.value.toUpperCase())}
                        placeholder="Ex: MARIA DA SILVA"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Validade *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.cardExpiry}
                        onChange={(e) => handleInputChange('cardExpiry', formatExpiry(e.target.value))}
                        placeholder="MM/AA"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CVV *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.cardCVV}
                        onChange={(e) => handleInputChange('cardCVV', e.target.value.replace(/\D/g, '').substring(0, 4))}
                        placeholder="Código de segurança"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Dados Pessoais */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Dados Pessoais
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CPF ou CNPJ *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.cpfCnpj}
                        onChange={(e) => handleInputChange('cpfCnpj', formatCpfCnpj(e.target.value))}
                        placeholder="Digite seu CPF ou CNPJ"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefone *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', formatPhone(e.target.value))}
                        placeholder="(00) 00000-0000"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                </div>

                {!hasBillingInfo && (
                <CheckoutZipCodeField
                  zipCode={formData.zipCode}
                  onZipCodeChange={handleZipCodeChange}
                  isValidating={isLoadingAddress}
                  validationStatus={zipValidationStatus}
                  title="Endereço de Cobrança"
                />
              )}

                

                {!isAuthenticated && !pixData && renderGuestIdentityBlock()}

                <CheckoutTermsAcceptance
                  checked={acceptedTerms}
                  onChange={setAcceptedTerms}
                  id="checkout-terms-card"
                />

                <button
                  type="submit"
                  form="card-form"
                  disabled={isProcessing || (!isAuthenticated && !isIdentityResolved) || !acceptedTerms}
                  className="w-full bg-[#5C528A] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#4A4070] transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processando...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      Finalizar Compra
                    </>
                  )}
                </button>

                <p className="text-sm text-gray-500 text-center">
                  <Lock className="w-4 h-4 inline mr-1" />
                  Pagamento seguro e criptografado
                </p>
              </form>

              </>
              )}
              </>
              )}
              </>
            </div>
          </div>

          {/* Resumo do Pedido */}
          <div className="order-2 min-w-0 lg:col-span-1 lg:order-2">
            <div className="max-w-full rounded-2xl bg-white p-4 shadow-lg sm:p-6 lg:sticky lg:top-24">
              <div className="mb-6 hidden lg:block">
                <CheckoutFounderTrust />
                <CheckoutPlatformPreview />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-6">Resumo do Pedido</h2>
              
              <div className="space-y-4 mb-6">
                <CheckoutPlanDetails
                  plan={plan}
                  paymentMethod={paymentMethod}
                  showPaymentMethod={!plan.isRecurring && !pixData}
                  effectiveQuantity={customQuantityState.supports ? customQuantityState.quantity : undefined}
                />

                {/* Campo de Cupom - PIX (não recorrente) não aceita cupom */}
                {!pixData && (
                  <div className="hidden lg:block border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cupom de Desconto
                    </label>
                    {!appliedCoupon ? (
                      <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          onBlur={handleCouponBlur}
                          placeholder="Digite o cupom"
                          disabled={isApplyingCoupon}
                          className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400 text-sm disabled:bg-gray-100"
                        />
                        <button
                          type="button"
                          onClick={applyCoupon}
                          disabled={isApplyingCoupon || !couponCode.trim()}
                          className="shrink-0 px-4 py-2 bg-[#5C528A] text-white rounded-lg font-semibold hover:bg-[#4A4070] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:whitespace-nowrap"
                        >
                          {isApplyingCoupon ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            'Aplicar'
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <div>
                              <p className="text-sm font-semibold text-green-900">{appliedCoupon?.code}</p>
                              <p className="text-xs text-green-700">
                                {appliedCoupon?.type === 'percentage'
                                  ? `${appliedCoupon.discount}% de desconto`
                                  : `${PlanService.formatCurrency(appliedCoupon?.discount ?? 0)} de desconto`
                                }
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={removeCoupon}
                            className="text-green-700 hover:text-green-900"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                    {plan.isRecurring && (
                      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                        <p className="text-xs font-medium text-amber-800">
                          Desconto do cupom válido para a primeira parcela do plano.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4">
                {customQuantityState.supports && (
                  <div className="mb-3 rounded-lg bg-[#5C528A]/5 px-3 py-3">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="text-xs font-medium uppercase tracking-wide text-[#5C528A]">
                        Quantidade de horas
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleChangeQuantity(-1)}
                          disabled={isLoading || customQuantityState.quantity <= customQuantityState.minQty}
                          aria-label="Diminuir 1 hora"
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#5C528A]/30 bg-white text-[#5C528A] font-bold hover:bg-[#5C528A] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          −
                        </button>
                        <span className="min-w-[2.5rem] text-center text-base font-bold text-gray-900 tabular-nums">
                          {customQuantityState.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleChangeQuantity(1)}
                          disabled={isLoading || customQuantityState.quantity >= customQuantityState.maxQty}
                          aria-label="Adicionar 1 hora"
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#5C528A]/30 bg-white text-[#5C528A] font-bold hover:bg-[#5C528A] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 text-sm">
                      <span className="text-gray-700">
                        {customQuantityState.quantity} {customQuantityState.quantity === 1 ? 'hora' : 'horas'} × {PlanService.formatCurrency(customQuantityState.pricePerUnit)}
                      </span>
                      <span className="font-semibold text-gray-900">
                        {PlanService.formatCurrency(purchaseBaseValue)}
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-500">
                      Mín. {customQuantityState.minQty} h · Máx. {customQuantityState.maxQty} h
                    </p>
                    {pixInvalidatedMessage && (
                      <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
                        {pixInvalidatedMessage}
                      </div>
                    )}
                  </div>
                )}
                {appliedCoupon && (
                  <>
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Subtotal:</span>
                      <span>{PlanService.formatCurrency(purchaseBaseValue)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-green-600 mb-3">
                      <span>Desconto{plan.isRecurring ? ' (1ª mensalidade)' : ''}:</span>
                      <span>
                        -{PlanService.formatCurrency(purchaseBaseValue - calculateFinalValue())}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <span className="text-lg font-semibold text-gray-900">
                    {plan.isRecurring && appliedCoupon ? '1ª mensalidade:' : 'Total:'}
                  </span>
                  <span className="text-2xl font-bold text-[#5C528A] sm:text-3xl">
                    {PlanService.formatCurrency(appliedCoupon ? calculateFinalValue() : purchaseBaseValue)}
                  </span>
                </div>
                {plan.isRecurring && appliedCoupon && (
                  <div className="mt-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className="text-sm font-medium text-gray-700">A partir do 2º mês:</span>
                    <span className="text-base font-semibold text-gray-900">
                      {PlanService.formatCurrency(plan.value)}<span className="text-xs font-normal text-gray-500">/mês</span>
                    </span>
                  </div>
                )}
                {plan.isRecurring && !appliedCoupon && (
                  <p className="text-xs text-gray-500 mt-2 text-right">
                    Cobrado mensalmente
                  </p>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-sm text-gray-900 mb-2">Informações Importantes:</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Pagamento processado de forma segura</li>
                  <li>• Você receberá um email de confirmação</li>
                  {plan.isRecurring && (
                    <li>• Cancele a qualquer momento pelo painel</li>
                  )}
                  {!plan.isRecurring && paymentMethod === 'pix' && (
                    <li>• Pagamento confirmado instantaneamente</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <InfoModal
        isOpen={modalState.isOpen}
        onClose={() => {
          setModalState({ ...modalState, isOpen: false });
          if (modalState.redirectTo) {
            navigate(modalState.redirectTo);
          }
        }}
        title={modalState.title}
        message={modalState.message}
        variant={modalState.variant}
        buttonText="Entendi"
      />
    </div>
  );
};

export default CheckoutPage;
